import { Suspense } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { UserButton } from '@clerk/clerk-react';
import { useSidebar } from '@/hooks/use-sidebar';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useHeartbeat } from '@/hooks/use-heartbeat';
import { Sidebar } from '@/components/sidebar/sidebar';
import { MobileMenu } from '@/components/sidebar/mobile-menu';
import { LanguageSwitcher } from '@/components/language-switcher';
import { PageErrorBoundary } from '@/components/page-error-boundary';
import { PageLoader } from '@/components/page-loader';
import { FeedbackPrompt } from '../features/feedback/feedback-prompt';
import { TrialBanner } from '@/components/trial-banner/trial-banner';
import { CompanySwitcher } from '@/components/company-switcher';
import { ReadOnlyBanner } from '@/components/read-only-banner';
import { Logo, Toaster } from '@bill-alps/ui';

export function AppLayout() {
  const { isCollapsed, toggle } = useSidebar();
  const { isAdmin, isSupport } = useCurrentUser();
  useHeartbeat();
  const location = useLocation();

  return (
    // The viewport lock — and the inner <main> scroller it creates — is desktop-only.
    // On mobile the document scrolls naturally, like every other route.
    <div className="flex min-h-[var(--app-viewport-svh)] flex-col overflow-x-clip bg-background md:h-[var(--app-viewport-svh)] md:max-h-[var(--app-viewport-svh)] md:overflow-hidden">
      <MobileMenu isAdmin={isAdmin} isSupport={isSupport} />

      {/* Desktop top bar — full width */}
      <header className="sticky top-0 z-40 hidden h-16 shrink-0 items-center justify-between border-b-2 border-border bg-background px-8 md:flex">
        <Link to="/" className="flex items-center gap-2">
          <Logo />
        </Link>
        <div className="flex items-center gap-4">
          <CompanySwitcher />
          <LanguageSwitcher />
          <UserButton />
        </div>
      </header>

      {/* Trial countdown / post-trial notice — spans full width below the bar */}
      <TrialBanner />

      {/* Sidebar + content row */}
      <div className="flex min-h-0 flex-1">
        {/* Desktop sidebar */}
        <Sidebar
          isCollapsed={isCollapsed}
          onToggle={toggle}
          isAdmin={isAdmin}
          className="hidden md:flex"
        />

        {/* `md:flex-col` lets a mounted split view (`data-split-view`) fill the
            remaining height below the banners; the `has-` variant then drops
            the padding so the split view's own gutters take over (lg+). */}
        <main
          data-error-host
          className="group/main relative min-w-0 flex-1 overflow-y-auto px-4 py-8 md:flex md:min-h-0 md:flex-col md:px-8 lg:has-[[data-split-view]]:p-0"
        >
          <ReadOnlyBanner />
          <PageErrorBoundary key={location.pathname}>
            {/* Every route below is code-split — see `lazyPage` in app.tsx. */}
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </PageErrorBoundary>
        </main>
      </div>
      <Toaster />
      <FeedbackPrompt />
    </div>
  );
}
